/**
 * Report Branding Guidelines — the house rules every produced document is set
 * in, applied by pointing at the thing you want to change.
 *
 * These are not decoration. The PDF profile reaches `institutional.py`'s
 * palette through a per-render context and the workbook profile themes the
 * Excel exports, so what this screen shows is what the document does.
 *
 * What it replaced, and why:
 *
 *   Every control was on screen at once — ten PDF colours, six workbook
 *   colours, three selects, three toggles, two text fields — in panels named
 *   after file formats, beside a specimen that could only be read. Changing
 *   the rule above a total meant knowing the value is called `rule` and that
 *   `rule` is filed under "PDF guidelines". The screen was organised by where
 *   a value is stored rather than by what a reader is looking at.
 *
 * Now: pick the report, click the part of it you want to change, change it,
 * watch the specimen redraw, save. The controls for everything you did NOT
 * click are not on screen, because they are not what you came to do.
 *
 * Deliberately absent: body ink and the type scale. Those belong to the print
 * system — a report whose every ink can be changed stops being one house's
 * document.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, FileText, Loader2, MousePointerClick, RefreshCw, RotateCcw, Save } from 'lucide-react'
import type { Loan } from '@/types'
import { reportsService } from '@/services/loans'
import {
  reportBrandingService,
  type PdfBrandProfile,
  type ReportBranding,
  type XlsxBrandProfile,
} from '@/services/reportBranding'
import {
  pipelineExportSettingsService,
  type PipelineExportSettings,
} from '@/services/pipelineExportSettings'
import { REPORT_TYPES, initialFilters, missingRequirement, reportTypeSpec } from './reportTypes'
import { BRAND_TARGETS, TARGETS_BY_ID, type BrandField, type BrandTarget } from './brandTargets'

/**
 * What the specimen imitates, per report kind.
 *
 * Keyed by the ids in `reportTypes.ts`, so the picker offers the reports this
 * desk actually produces and the preview is of THEIR document rather than of
 * a generic one. A kind with no entry here still previews — it falls back to
 * the committee specimen — so adding a report type cannot blank this screen.
 *
 * Every name is an obvious placeholder ("Sample Tower"). The specimens used to
 * attach invented figures ($41.5M, 62.0% LTV, "Pass") to the client's real
 * deal name Reunion Square, and to names beside live deals (EXEC-24).
 */
type Specimen = {
  eyebrow: string
  title: string
  metric: string
  metricLabel: string
  columns: [string, string, string]
  rows: [string, string, string][]
  total: [string, string, string]
}

const SPECIMENS: Record<string, Specimen> = {
  committee_book: {
    eyebrow: 'Committee Book',
    title: 'Sample Tower',
    metric: '$41.5M',
    metricLabel: 'Commitment',
    columns: ['Asset', 'Amount', 'LTV'],
    rows: [
      ['Sample Tower', '$41.5M', '62.0%'],
      ['Sample Court', '$18.3M', '58.2%'],
      ['Sample Yards', '$61.8M', '66.4%'],
    ],
    total: ['Total', '$121.6M', '62.2%'],
  },
  pipeline: {
    eyebrow: 'Pipeline Report',
    title: 'Origination Pipeline',
    metric: '$1.24B',
    metricLabel: 'In market',
    columns: ['Deal', 'Amount', 'Stage'],
    rows: [
      ['Sample Tower', '$41.5M', 'Underwriting'],
      ['Sample Commons', '$27.2M', 'Term sheet'],
      ['Sample Yards', '$61.8M', 'Committee'],
    ],
    total: ['Total', '$130.5M', '—'],
  },
  dossier: {
    eyebrow: 'Complete Deal Lifecycle',
    title: 'Sample Tower',
    metric: '1.42x',
    metricLabel: 'DSCR (in place)',
    columns: ['Metric', 'In place', 'Stabilised'],
    rows: [
      ['NOI', '$3.61M', '$4.18M'],
      ['Debt service', '$2.54M', '$2.54M'],
      ['Occupancy', '91.2%', '95.0%'],
    ],
    total: ['DSCR', '1.42x', '1.65x'],
  },
  screener: {
    eyebrow: 'Screener',
    title: 'Credit Box — September',
    metric: '12 of 31',
    metricLabel: 'Inside the box',
    columns: ['Deal', 'LTV', 'Verdict'],
    rows: [
      ['Sample Tower', '62.0%', 'Pass'],
      ['Sample Commons', '74.8%', 'Refer'],
      ['Sample Flats', '81.3%', 'Decline'],
    ],
    total: ['Passing', '12', '38.7%'],
  },
  asset_management: {
    eyebrow: 'Asset Management Report',
    title: 'Watchlist — September',
    metric: '4',
    metricLabel: 'On watch',
    columns: ['Asset', 'DSCR', 'Tier'],
    rows: [
      ['Sample Yards', '1.02x', 'Heightened'],
      ['Sample Commons', '0.94x', 'Workout'],
      ['Sample Court', '1.18x', 'Routine'],
    ],
    total: ['Weighted', '1.05x', '—'],
  },
  pipeline_changes: {
    eyebrow: 'Pipeline Changes',
    title: 'Sample Week',
    metric: '3',
    metricLabel: 'Kills',
    columns: ['Deal', 'From', 'To'],
    rows: [
      ['Sample Tower', 'Term Sheet Issued', 'Term Sheet Signed'],
      ['Sample Commons', 'Post-screen Quote', 'Term Sheet Issued'],
      ['Sample Flats', 'Term Sheet Signed', 'Post-screen Quote'],
    ],
    total: ['Moves', '3', '—'],
  },
}

const DEFAULT_SPECIMEN = SPECIMENS.committee_book

export function ReportingSettings({ deals = [] }: { deals?: Loan[] }) {
  const queryClient = useQueryClient()
  const brandingQuery = useQuery({
    queryKey: ['report-branding'],
    queryFn: () => reportBrandingService.get(),
    staleTime: 60_000,
  })
  const pipelineQuery = useQuery({
    queryKey: ['pipeline-export-settings'],
    queryFn: () => pipelineExportSettingsService.get(),
    staleTime: 60_000,
  })

  const [draft, setDraft] = useState<ReportBranding | null>(null)
  const [pipelineDraft, setPipelineDraft] = useState<PipelineExportSettings | null>(null)
  const [reportId, setReportId] = useState<string>('committee_book')
  const [selectedTarget, setSelectedTarget] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (brandingQuery.data && !draft) setDraft(structuredClone(brandingQuery.data))
  }, [brandingQuery.data, draft])
  useEffect(() => {
    if (pipelineQuery.data && !pipelineDraft) setPipelineDraft({ ...pipelineQuery.data })
  }, [pipelineDraft, pipelineQuery.data])

  const dirty = useMemo(
    () =>
      Boolean(draft && brandingQuery.data && JSON.stringify(draft) !== JSON.stringify(brandingQuery.data)),
    [brandingQuery.data, draft],
  )
  const pipelineDirty = useMemo(
    () =>
      Boolean(
        pipelineDraft &&
          pipelineQuery.data &&
          JSON.stringify(pipelineDraft) !== JSON.stringify(pipelineQuery.data),
      ),
    [pipelineDraft, pipelineQuery.data],
  )

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!draft) throw new Error('Nothing to save.')
      const saved = await reportBrandingService.update(draft)
      if (pipelineDraft && pipelineDirty) {
        await pipelineExportSettingsService.update(pipelineDraft)
      }
      return saved
    },
    onSuccess: async (saved) => {
      setDraft(structuredClone(saved))
      setNotice('Brand guidelines saved. Every report produced from now on is set in them.')
      setError(null)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['report-branding'] }),
        queryClient.invalidateQueries({ queryKey: ['pipeline-export-settings'] }),
      ])
    },
    onError: (cause) => {
      const detail = (cause as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(detail ?? (cause instanceof Error ? cause.message : 'The guidelines could not be saved.'))
      setNotice(null)
    },
  })

  if (brandingQuery.isLoading || !draft) {
    return (
      <div className="flex h-full min-h-[240px] items-center justify-center gap-2 text-[12px] text-stone-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading brand guidelines…
      </div>
    )
  }

  /** One write path for every control, whatever surface it belongs to. */
  function setField(field: BrandField, value: string | boolean) {
    setDraft((current) => {
      if (!current) return current
      return field.surface === 'pdf'
        ? { ...current, pdf: { ...current.pdf, [field.name]: value } as PdfBrandProfile }
        : { ...current, xlsx: { ...current.xlsx, [field.name]: value } as XlsxBrandProfile }
    })
  }

  const target = selectedTarget ? TARGETS_BY_ID[selectedTarget] ?? null : null
  const specimen = SPECIMENS[reportId] ?? DEFAULT_SPECIMEN

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="reporting-settings">
      <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-amber-400/12 bg-[#11100e] px-3 py-2">
        <div>
          <h2 className="text-[13px] font-semibold text-stone-100">Report Branding Guidelines</h2>
          <p className="text-[10px] text-stone-500">
            Pick a report, click the part you want to change, then save.
          </p>
        </div>

        <label className="flex items-center gap-2 text-[10px] text-stone-400">
          Report
          <select
            aria-label="Report to preview"
            value={reportId}
            onChange={(event) => setReportId(event.target.value)}
            className="rounded border border-amber-400/20 bg-[#141210] px-2 py-1 text-[11px] text-stone-200 focus:border-amber-400/50 focus:outline-none"
          >
            {REPORT_TYPES.map((spec) => (
              <option key={spec.id} value={spec.id}>
                {spec.name}
              </option>
            ))}
          </select>
        </label>

        <div className="ml-auto flex items-center gap-2">
          {dirty || pipelineDirty ? (
            <span className="text-[10px] text-amber-200/80">Unsaved changes</span>
          ) : null}
          <button
            type="button"
            onClick={() => {
              if (brandingQuery.data) setDraft(structuredClone(brandingQuery.data))
              if (pipelineQuery.data) setPipelineDraft({ ...pipelineQuery.data })
              setNotice(null)
              setError(null)
            }}
            disabled={!dirty && !pipelineDirty}
            className="reporting-secondary !px-2 !py-1 text-[10px] disabled:opacity-40"
          >
            <RotateCcw className="h-3 w-3" /> Discard
          </button>
          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || (!dirty && !pipelineDirty)}
            className="reporting-primary !px-2 !py-1 text-[10px] disabled:opacity-40"
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Save className="h-3 w-3" />
            )}
            Apply to every report
          </button>
        </div>
      </div>

      {(notice || error) && (
        <div
          role={error ? 'alert' : 'status'}
          className={`mx-3 mt-3 shrink-0 rounded border px-3 py-2 text-[11px] ${
            error
              ? 'border-red-400/30 bg-red-500/10 text-red-200'
              : 'border-emerald-400/25 bg-emerald-500/10 text-emerald-200'
          }`}
        >
          {error ?? notice}
        </div>
      )}

      <div className="grid min-h-0 flex-1 gap-3 overflow-hidden p-3 xl:grid-cols-[minmax(0,1fr)_320px]">
        <section className="grid min-h-0 gap-4 overflow-auto rounded-lg border border-amber-400/15 bg-[#0d0d0d] p-4 lg:grid-cols-[minmax(300px,380px)_minmax(0,1fr)]">
          {/* Left: the map of what can be changed. Right: the actual page.
              The map is not the document — it names the parts; the document
              shows the result. Keeping both on screen is what stops "which
              part is this?" and "did that change anything?" being two
              different screens. */}
          <div className="min-w-0">
            <p className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[.14em] text-amber-300/85">
              <MousePointerClick className="h-3 w-3" /> Click a part to change it
            </p>
            <p className="mb-3 text-[10px] text-stone-500">
              A map of the marks, painted in the values on screen. The real page is on the right.
            </p>
            <BrandSpecimen
              pdf={draft.pdf}
              xlsx={draft.xlsx}
              specimen={specimen}
              selected={selectedTarget}
              onSelect={setSelectedTarget}
            />
          </div>
          <BrandDocumentPreview reportId={reportId} pdf={draft.pdf} deals={deals} />
        </section>

        <Inspector
          target={target}
          draft={draft}
          onChange={setField}
          onClear={() => setSelectedTarget(null)}
          reportId={reportId}
          defaultFormat={draft.output.defaultFormat[reportId as keyof typeof draft.output.defaultFormat] ?? 'pdf'}
          formats={REPORT_TYPES.find((spec) => spec.id === reportId)?.formats ?? ['pdf']}
          onDefaultFormat={(format) =>
            setDraft((current) =>
              current
                ? {
                    ...current,
                    output: {
                      ...current.output,
                      defaultFormat: { ...current.output.defaultFormat, [reportId]: format },
                    },
                  }
                : current,
            )
          }
          pipelineDraft={pipelineDraft}
          onPipelineDraft={setPipelineDraft}
        />
      </div>
    </div>
  )
}

/**
 * The controls for the one mark that is selected, and nothing else.
 *
 * With nothing selected this is a prompt rather than a blank: an empty panel
 * beside a document reads as a page that failed to load.
 */
function Inspector({
  target,
  draft,
  onChange,
  onClear,
  reportId,
  defaultFormat,
  formats,
  onDefaultFormat,
  pipelineDraft,
  onPipelineDraft,
}: {
  target: BrandTarget | null
  draft: ReportBranding
  onChange: (field: BrandField, value: string | boolean) => void
  onClear: () => void
  reportId: string
  defaultFormat: 'pdf' | 'xlsx'
  formats: readonly string[]
  onDefaultFormat: (format: 'pdf' | 'xlsx') => void
  pipelineDraft: PipelineExportSettings | null
  onPipelineDraft: (next: PipelineExportSettings) => void
}) {
  return (
    <aside className="min-h-0 overflow-auto rounded-lg border border-amber-400/15 bg-[#0d0d0d]" aria-label="Formatting">
      {target ? (
        <div data-testid="brand-inspector">
          <div className="sticky top-0 border-b border-amber-400/12 bg-[#11100e] px-3 py-2">
            <div className="flex items-center gap-2">
              <h3 className="text-[12px] font-semibold text-amber-200">{target.label}</h3>
              <span className="rounded border border-amber-400/20 px-1.5 py-px text-[9px] uppercase tracking-[.12em] text-stone-500">
                {target.surface === 'pdf' ? 'PDF' : 'Excel'}
              </span>
              <button
                type="button"
                onClick={onClear}
                className="ml-auto text-[10px] text-stone-500 hover:text-stone-300"
              >
                Done
              </button>
            </div>
            <p className="mt-0.5 text-[10px] leading-relaxed text-stone-500">{target.note}</p>
          </div>
          <div className="space-y-3 p-3">
            {target.fields.map((field) => (
              <FieldControl
                key={`${field.surface}.${field.name}`}
                field={field}
                value={
                  field.surface === 'pdf'
                    ? (draft.pdf as unknown as Record<string, string | boolean>)[field.name]
                    : (draft.xlsx as unknown as Record<string, string | boolean>)[field.name]
                }
                onChange={(value) => onChange(field, value)}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="p-3">
          <div className="rounded border border-dashed border-amber-400/20 bg-amber-400/[0.03] px-3 py-6 text-center">
            <MousePointerClick className="mx-auto h-4 w-4 text-amber-300/70" />
            <p className="mt-2 text-[11px] text-stone-300">Nothing selected</p>
            <p className="mt-1 text-[10px] leading-relaxed text-stone-500">
              Click a heading, a table, the metric strip or the footer in the specimen and its
              controls appear here.
            </p>
          </div>

          <div className="mt-4 border-t border-amber-400/10 pt-3">
            <h3 className="text-[11px] font-medium text-stone-300">This report’s default</h3>
            <p className="mt-0.5 text-[10px] text-stone-500">
              What {REPORT_TYPES.find((spec) => spec.id === reportId)?.name ?? 'this report'}{' '}
              produces unless a run asks for the other.
            </p>
            <div className="mt-2 flex gap-1.5">
              {formats.map((format) => (
                <button
                  key={format}
                  type="button"
                  aria-pressed={defaultFormat === format}
                  onClick={() => onDefaultFormat(format as 'pdf' | 'xlsx')}
                  className={`rounded border px-2 py-1 text-[10px] uppercase tracking-wide transition-colors ${
                    defaultFormat === format
                      ? 'border-amber-400/45 bg-amber-400/[0.12] text-amber-200'
                      : 'border-amber-400/15 text-stone-400 hover:text-stone-200'
                  }`}
                >
                  {format}
                </button>
              ))}
            </div>
          </div>

          {pipelineDraft && (
            <div className="mt-4 border-t border-amber-400/10 pt-3">
              <h3 className="text-[11px] font-medium text-stone-300">Pipeline toolbar exports</h3>
              <p className="mt-0.5 text-[10px] text-stone-500">
                What the Pipeline page’s one-click Print / PDF / Excel icons render.
              </p>
              <label className="mt-2 block text-[10px] text-stone-400">
                Layout
                <select
                  aria-label="Pipeline export layout"
                  value={pipelineDraft.layout}
                  onChange={(event) =>
                    onPipelineDraft({
                      ...pipelineDraft,
                      layout: event.target.value as PipelineExportSettings['layout'],
                    })
                  }
                  className="mt-1 w-full rounded border border-amber-400/20 bg-[#141210] px-2 py-1 text-[11px] text-stone-200 focus:border-amber-400/50 focus:outline-none"
                >
                  <option value="backshop">Backshop — 26 columns, deals under their stage</option>
                  <option value="legacy">Legacy — the template and columns below</option>
                </select>
              </label>
              {pipelineDraft.layout === 'backshop' ? (
                <p className="mt-2 text-[10px] leading-relaxed text-stone-500">
                  Print, PDF and Excel always export the Backshop layout, whatever columns the screen
                  shows. The rows are the deals the view’s filters select.
                </p>
              ) : (
                <>
                  <label className="mt-2 block text-[10px] text-stone-400">
                    Template
                    <select
                      aria-label="Pipeline export template"
                      value={pipelineDraft.template}
                      onChange={(event) =>
                        onPipelineDraft({
                          ...pipelineDraft,
                          template: event.target.value as PipelineExportSettings['template'],
                        })
                      }
                      className="mt-1 w-full rounded border border-amber-400/20 bg-[#141210] px-2 py-1 text-[11px] text-stone-200 focus:border-amber-400/50 focus:outline-none"
                    >
                      <option value="pipeline_summary">Pipeline Summary</option>
                      <option value="pipeline_daily">Daily Pipeline (change bar)</option>
                    </select>
                  </label>
                  <Toggle
                    label="Mirror the on-screen columns"
                    help="Off renders the fixed, client-matched template regardless of filters."
                    checked={pipelineDraft.useOnScreenColumns}
                    onChange={(value) =>
                      onPipelineDraft({ ...pipelineDraft, useOnScreenColumns: value })
                    }
                  />
                </>
              )}
            </div>
          )}

          <p className="mt-4 border-t border-amber-400/10 pt-3 text-[10px] leading-relaxed text-stone-500">
            Body type and the type scale are set by the print system and are not configurable — a
            report whose every ink can be changed stops being one house’s document.
          </p>
        </div>
      )}
    </aside>
  )
}

/** One control, drawn for whatever kind of value it carries. */
function FieldControl({
  field,
  value,
  onChange,
}: {
  field: BrandField
  value: string | boolean | undefined
  onChange: (value: string | boolean) => void
}) {
  const unseen = field.unseen ? (
    <span className="mt-1 block text-[9px] italic text-stone-600">
      Applied to the produced file; the specimen does not draw it.
    </span>
  ) : null

  if (field.kind === 'toggle') {
    return (
      <div>
        <Toggle
          label={field.label}
          help={field.help}
          checked={Boolean(value)}
          onChange={onChange}
        />
        {unseen}
      </div>
    )
  }

  if (field.kind === 'select') {
    return (
      <label className="block text-[10px] text-stone-400">
        {field.label}
        <select
          aria-label={field.label}
          value={String(value ?? '')}
          onChange={(event) => onChange(event.target.value)}
          className="mt-1 w-full rounded border border-amber-400/20 bg-[#141210] px-2 py-1 text-[11px] text-stone-200 focus:border-amber-400/50 focus:outline-none"
        >
          {field.options.map(([id, label]) => (
            <option key={id} value={id}>
              {label}
            </option>
          ))}
        </select>
        {field.help && <span className="mt-1 block text-[9px] text-stone-600">{field.help}</span>}
        {unseen}
      </label>
    )
  }

  if (field.kind === 'text') {
    return (
      <label className="block text-[10px] text-stone-400">
        {field.label}
        <input
          aria-label={field.label}
          value={String(value ?? '')}
          onChange={(event) => onChange(event.target.value)}
          className="mt-1 w-full rounded border border-amber-400/20 bg-[#141210] px-2 py-1 text-[11px] text-stone-200 focus:border-amber-400/50 focus:outline-none"
        />
        {field.help && <span className="mt-1 block text-[9px] text-stone-600">{field.help}</span>}
        {unseen}
      </label>
    )
  }

  return (
    <div className="text-[10px] text-stone-400">
      {field.label}
      <div className="mt-1 flex items-center gap-2">
        <input
          type="color"
          aria-label={field.label}
          value={normaliseHex(String(value ?? '#000000'))}
          onChange={(event) => onChange(event.target.value)}
          className="h-7 w-9 cursor-pointer rounded border border-amber-400/20 bg-transparent p-0.5"
        />
        <input
          aria-label={`${field.label} hex`}
          value={String(value ?? '')}
          onChange={(event) => onChange(event.target.value)}
          className="w-full rounded border border-amber-400/20 bg-[#141210] px-2 py-1 font-mono text-[11px] uppercase text-stone-200 focus:border-amber-400/50 focus:outline-none"
        />
      </div>
      {field.help && <span className="mt-1 block text-[9px] text-stone-600">{field.help}</span>}
      {unseen}
    </div>
  )
}

/** `<input type="color">` refuses anything but #rrggbb, and refusing silently
 *  leaves the swatch black beside a hex field showing the real value. */
function normaliseHex(value: string): string {
  const text = value.trim()
  if (/^#[0-9a-f]{6}$/i.test(text)) return text
  if (/^#[0-9a-f]{3}$/i.test(text)) {
    return `#${text[1]}${text[1]}${text[2]}${text[2]}${text[3]}${text[3]}`
  }
  return '#000000'
}

function Toggle({
  label,
  help,
  checked,
  onChange,
}: {
  label: string
  help?: string
  checked: boolean
  onChange: (value: boolean) => void
}) {
  return (
    <label className="mt-2 flex cursor-pointer items-start gap-2 text-[10px] text-stone-400">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 h-3 w-3 accent-amber-400"
      />
      <span>
        {label}
        {help && <span className="mt-0.5 block text-[9px] text-stone-600">{help}</span>}
      </span>
    </label>
  )
}

/**
 * A page and a sheet, painted in the values on screen — and clickable.
 *
 * Every region carries the id of the target that governs it, so pointing at a
 * mark and editing it are the same gesture.
 */
/**
 * The document itself — the real PDF from the same builders that produce the
 * delivered file, rendered inside the UNSAVED guidelines on screen.
 *
 * The specimen beside it is the control surface (click a part, change it);
 * this is the proof. It redraws, debounced, on every change to the PDF
 * profile, so the person changing a colour sees the actual page take it
 * before they press Apply. Nothing here is persisted and no generation is
 * recorded: /reports/preview is a look, not a delivery.
 */
function BrandDocumentPreview({
  reportId,
  pdf,
  deals,
}: {
  reportId: string
  pdf: PdfBrandProfile
  deals: Loan[]
}) {
  const spec = reportTypeSpec(reportId)
  // A deal-scoped report is shown on the first deal on the book: the point
  // is the typesetting, not which deal.
  const filters = useMemo(() => {
    const base = initialFilters(spec.id)
    const needsDeal = spec.fields.some((field) => field.key === 'loan_id' && field.required) || spec.id === 'screener'
    return needsDeal && deals[0] ? { ...base, loan_id: deals[0].id } : base
  }, [deals, spec])
  const blocked = missingRequirement(spec, filters)

  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [rendering, setRendering] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [stale, setStale] = useState(false)
  const requestSeq = useRef(0)
  const objectUrl = useRef<string | null>(null)

  const fingerprint = useMemo(
    () => JSON.stringify({ template: spec.engineTemplate, filters, pdf }),
    [filters, pdf, spec.engineTemplate],
  )

  const render = useCallback(async () => {
    if (blocked) return
    const seq = ++requestSeq.current
    setRendering(true)
    setError(null)
    try {
      const response = await reportsService.preview({
        template: spec.engineTemplate,
        filters,
        branding: { pdf },
      })
      if (seq !== requestSeq.current) return
      const blob = response.data as Blob
      if (!(blob instanceof Blob) || blob.size === 0) throw new Error('The engine returned an empty document.')
      if (typeof URL.createObjectURL !== 'function') return
      const next = URL.createObjectURL(blob)
      if (objectUrl.current) URL.revokeObjectURL(objectUrl.current)
      objectUrl.current = next
      setPdfUrl(`${next}#toolbar=0&navpanes=0&view=FitH`)
      setStale(false)
    } catch (cause) {
      if (seq !== requestSeq.current) return
      const detail = (cause as { response?: { data?: unknown } })?.response?.data
      let message = cause instanceof Error ? cause.message : 'The document could not be rendered.'
      if (detail instanceof Blob) {
        try {
          const parsed = JSON.parse(await detail.text()) as { detail?: string }
          if (parsed?.detail) message = parsed.detail
        } catch {
          /* keep the generic message */
        }
      }
      setError(message)
    } finally {
      if (seq === requestSeq.current) setRendering(false)
    }
  }, [blocked, filters, pdf, spec.engineTemplate])

  // Debounced: a colour picker fires on every pixel of a drag.
  useEffect(() => {
    if (blocked) return
    setStale(true)
    const handle = window.setTimeout(() => void render(), 700)
    return () => window.clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint, blocked])

  useEffect(
    () => () => {
      if (objectUrl.current) URL.revokeObjectURL(objectUrl.current)
    },
    [],
  )

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="brand-document-preview">
      <div className="mb-2 flex shrink-0 items-center gap-2">
        <FileText className="h-3.5 w-3.5 text-amber-300/80" />
        <span className="text-[10px] font-semibold uppercase tracking-[.14em] text-amber-300/85">
          The document, as it will print
        </span>
        <span className="text-[10px] text-stone-500">
          {rendering ? 'Redrawing…' : stale ? 'Changes pending…' : 'Up to date with the values on screen'}
        </span>
        <button
          type="button"
          onClick={() => void render()}
          disabled={rendering || Boolean(blocked)}
          className="reporting-secondary ml-auto !px-2 !py-1 text-[10px] disabled:opacity-40"
        >
          {rendering ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Redraw
        </button>
      </div>
      {blocked ? (
        <div className="flex flex-1 items-center justify-center rounded border border-amber-400/15 bg-[#141210] p-6 text-center text-[11px] text-stone-400">
          {deals.length === 0
            ? 'This report is about one deal, and there is no deal on the book yet to set it on. The specimen still shows every mark.'
            : blocked}
        </div>
      ) : error ? (
        <div
          role="alert"
          className="flex flex-1 items-center justify-center gap-2 rounded border border-red-400/25 bg-red-500/10 p-6 text-center text-[11px] text-red-200"
        >
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> {error}
        </div>
      ) : pdfUrl ? (
        <iframe
          title={`${spec.name} — as it will print`}
          src={pdfUrl}
          className="min-h-[520px] flex-1 rounded border border-amber-400/15 bg-white"
        />
      ) : (
        <div className="flex min-h-[520px] flex-1 items-center justify-center gap-2 rounded border border-amber-400/15 bg-[#141210] text-[11px] text-stone-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Rendering the document…
        </div>
      )}
    </div>
  )
}
function BrandSpecimen({
  pdf,
  xlsx,
  specimen,
  selected,
  onSelect,
}: {
  pdf: PdfBrandProfile
  xlsx: XlsxBrandProfile
  specimen: Specimen
  selected: string | null
  onSelect: (id: string) => void
}) {
  const banded = pdf.tableRules !== 'ruled'
  /** `full` because a region that fills its cell needs the width and the small
   *  page chip does not; without it the chip stretches across the specimen. */
  const region = (id: string, full = true) => ({
    'data-brand-target': id,
    'aria-label': `Format ${TARGETS_BY_ID[id]?.label ?? id}`,
    'aria-pressed': selected === id,
    onClick: (event: React.MouseEvent) => {
      event.stopPropagation()
      onSelect(id)
    },
    className: `relative cursor-pointer text-left outline-none transition-shadow ${
      full ? 'w-full' : ''
    } ${
      selected === id
        ? 'shadow-[0_0_0_2px_#F5B301]'
        : 'hover:shadow-[0_0_0_2px_rgba(245,179,1,.45)]'
    }`,
  })

  return (
    <div className="mx-auto max-w-[680px]">
      {/* The sheet itself is a region like any other, but it CONTAINS the
          others — so it gets its own control above the page rather than
          wrapping it. Nesting one button inside another is invalid, and the
          inner mark stops being clickable. */}
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-[9px] uppercase tracking-[.14em] text-stone-600">Specimen · sample figures, not a deal</span>
        <button type="button" {...region('page', false)}>
          <span className="inline-block px-2 py-0.5 text-[9px] uppercase tracking-[.14em] text-stone-500">
            Page and marks
          </span>
        </button>
      </div>
      <div className="w-full">
        <div className="bg-white text-[#1B1F24] shadow-lg">
          <div className="px-4 pb-3 pt-4" style={{ borderBottom: `2px solid ${pdf.accent}` }}>
            <button type="button" {...region('cover-eyebrow')}>
              <span
                className="block text-[8px] font-semibold uppercase tracking-[0.18em]"
                style={{ color: pdf.accent }}
              >
                {specimen.eyebrow}
              </span>
            </button>
            <button type="button" {...region('cover-title')}>
              <span
                className="mt-1 block text-[18px] font-bold leading-tight"
                style={{
                  color: pdf.display,
                  fontFamily: pdf.fontFamily === 'times' ? 'Georgia, serif' : undefined,
                }}
              >
                {specimen.title}
              </span>
            </button>
            <button type="button" {...region('metric-strip')}>
              <span
                className="mt-2 block px-2 py-1.5"
                style={{ backgroundColor: pdf.accentPale, borderTop: `1.5px solid ${pdf.accent}` }}
              >
                <span className="text-[13px] font-bold" style={{ color: pdf.display }}>
                  {specimen.metric}
                </span>
                <span className="ml-1 text-[7px] font-semibold uppercase tracking-[0.12em] text-[#6E7781]">
                  {specimen.metricLabel}
                </span>
              </span>
            </button>
          </div>

          <table className="w-full border-collapse text-[9px]">
            <thead>
              <tr
                style={
                  banded
                    ? { backgroundColor: pdf.tableHeadBg, color: pdf.tableHeadText }
                    : { borderBottom: `1.5px solid ${pdf.display}`, color: '#6E7781' }
                }
              >
                {specimen.columns.map((column, index) => (
                  <th
                    key={column}
                    className={`p-0 uppercase tracking-wide ${index === 0 ? 'text-left' : 'text-right'}`}
                  >
                    <button type="button" {...region('table-head')}>
                      <span className={`block px-2 py-1 ${index === 0 ? 'text-left' : 'text-right'}`}>
                        {column}
                      </span>
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {specimen.rows.map((row, index) => (
                <tr
                  key={row[0]}
                  style={{
                    borderBottom: `0.5px solid ${pdf.hairline}`,
                    backgroundColor: pdf.zebra && index % 2 === 1 ? pdf.zebraBg : undefined,
                  }}
                >
                  {row.map((cell, cellIndex) => (
                    <td key={cell} className="p-0">
                      <button type="button" {...region('table-rows')}>
                        <span
                          className={`block px-2 py-1 ${cellIndex === 0 ? 'text-left' : 'text-right tabular-nums'}`}
                        >
                          {cell}
                        </span>
                      </button>
                    </td>
                  ))}
                </tr>
              ))}
              <tr style={{ borderTop: `1px solid ${pdf.rule}`, fontWeight: 700 }}>
                {specimen.total.map((cell, index) => (
                  <td key={cell} className="p-0">
                    <button type="button" {...region('table-total')}>
                      <span
                        className={`block px-2 py-1 ${index === 0 ? 'text-left' : 'text-right tabular-nums'}`}
                      >
                        {cell}
                      </span>
                    </button>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>

          <button type="button" {...region('footer')}>
            <span
              className="mt-2 flex items-center justify-between px-4 py-1.5 text-[7px]"
              style={{ backgroundColor: pdf.displayDeep, color: '#FFFFFF' }}
            >
              <span>{pdf.confidentialityLine || 'Private and confidential'}</span>
              <span>{pdf.footerCentre}</span>
            </span>
          </button>
        </div>
      </div>

      <div className="mt-4 overflow-hidden rounded border border-stone-800">
        <div className="bg-[#141414] px-2 py-1 text-[8px] uppercase tracking-[0.14em] text-stone-500">
          Workbook export
        </div>
        <table className="w-full border-collapse text-[9px]">
          <thead>
            <tr style={{ backgroundColor: xlsx.headerBg, color: xlsx.headerText }}>
              {specimen.columns.slice(0, 2).map((column, index) => (
                <th key={column} className="p-0">
                  <button type="button" {...region('xlsx-head')}>
                    <span className={`block px-2 py-1 ${index === 0 ? 'text-left' : 'text-right'}`}>
                      {column}
                    </span>
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* The same report, exported. A sheet showing different deals from
                the page above reads as a second, unrelated sample. */}
            {specimen.rows.slice(0, 2).map((row, index) => (
              <tr
                key={row[0]}
                style={{
                  backgroundColor: xlsx.zebra && index % 2 === 1 ? xlsx.rowAltBg : xlsx.rowBg,
                  color: xlsx.rowText,
                  borderBottom: xlsx.gridlines ? `0.5px solid ${xlsx.gridBorder}` : undefined,
                }}
              >
                {row.slice(0, 2).map((cell, cellIndex) => (
                  <td key={cell} className="p-0">
                    <button type="button" {...region(cellIndex === 1 ? 'xlsx-grid' : 'xlsx-rows')}>
                      <span
                        className={`block px-2 py-1 ${cellIndex === 0 ? 'text-left' : 'text-right tabular-nums'}`}
                      >
                        {cell}
                      </span>
                    </button>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-[10px] text-stone-500">
        {BRAND_TARGETS.length} parts of the document can be restyled here.
      </p>
    </div>
  )
}
